<template>
  <div class="relative h-full lg:mt-0 -mt-4 bg-white rounded-t-xl flex flex-wrap">
    <nav-menu />

    <!-- Header -->
    <div class="w-full bg-gray-50 border-b border-gray-200">
      <div class="px-6 py-4">
        <div class="flex flex-wrap items-center gap-4">
          <h1 class="text-lg font-semibold text-gray-900 mr-4">Dashboard</h1>

          <!-- Date inputs -->
          <div class="flex items-center gap-3">
            <span class="text-sm text-gray-500 font-medium">From</span>
            <datetime
              v-model="dateFrom"
              type="date"
              input-class="bg-white border border-gray-200 rounded-lg text-base text-gray-800 px-4 outline-none cursor-pointer w-44"
              class="theme-orange"
              style="height:46px;display:flex;align-items:center"
            ></datetime>
            <span class="text-sm text-gray-500 font-medium">To</span>
            <datetime
              v-model="dateTo"
              type="date"
              input-class="bg-white border border-gray-200 rounded-lg text-base text-gray-800 px-4 outline-none cursor-pointer w-44"
              class="theme-orange"
              style="height:46px;display:flex;align-items:center"
            ></datetime>
          </div>

          <!-- Weekend toggles -->
          <div class="flex items-center gap-4">
            <label class="inline-flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer select-none" style="height:40px" @click.prevent="includeSaturday = !includeSaturday">
              <span
                class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200"
                :class="includeSaturday ? 'bg-primary' : 'bg-gray-300'"
                style="width:44px;height:24px"
              >
                <span
                  class="inline-block bg-white rounded-full shadow transition-transform duration-200"
                  style="width:20px;height:20px"
                  :style="{ transform: includeSaturday ? 'translateX(22px)' : 'translateX(2px)' }"
                ></span>
              </span>
              Sat
            </label>
            <label class="inline-flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer select-none" style="height:40px" @click.prevent="includeSunday = !includeSunday">
              <span
                class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200"
                :class="includeSunday ? 'bg-primary' : 'bg-gray-300'"
                style="width:44px;height:24px"
              >
                <span
                  class="inline-block bg-white rounded-full shadow transition-transform duration-200"
                  style="width:20px;height:20px"
                  :style="{ transform: includeSunday ? 'translateX(22px)' : 'translateX(2px)' }"
                ></span>
              </span>
              Sun
            </label>
          </div>

          <!-- Quick-select buttons -->
          <div class="flex items-center gap-2 ml-auto flex-wrap">
            <button
              v-for="preset in presets"
              :key="preset.label"
              @click="applyPreset(preset)"
              class="px-4 rounded-lg text-sm font-medium border transition-colors"
              :class="activePreset === preset.label
                ? 'bg-primary text-white border-primary'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'"
              style="height:40px"
            >{{ preset.label }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile tab bar -->
    <div class="lg:hidden w-full bg-white border-b border-gray-200 px-4 py-2">
      <div class="flex gap-2 overflow-x-auto no-scrollbar">
        <router-link
          v-for="item in navItems"
          :key="item.route"
          :to="{ name: item.route }"
          class="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
          :class="$route.name === item.route
            ? 'bg-primary text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
        >
          {{ item.label }}
          <i class="fa-solid fa-chevron-right text-[10px] opacity-60"></i>
        </router-link>
      </div>
    </div>

    <!-- Body -->
    <div class="h-full bg-gray-100 w-full">
      <div class="flex w-full">
        <!-- Desktop sidebar (far left, sticky with scroll) -->
        <aside class="hidden lg:block w-56 flex-shrink-0">
          <div class="sticky top-0 h-screen overflow-y-auto bg-white border-r border-gray-200 py-6">
            <nav class="space-y-1 px-3">
              <router-link
                v-for="item in navItems"
                :key="item.route"
                :to="{ name: item.route }"
                class="flex items-center justify-between px-4 py-3 rounded-lg text-base font-medium transition-colors group"
                :class="$route.name === item.route
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'"
              >
                <div>
                  <span>{{ item.label }}</span>
                  <p class="text-xs font-normal mt-0.5" :class="$route.name === item.route ? 'text-gray-500' : 'text-gray-400'">{{ item.helper }}</p>
                </div>
                <i class="fa-solid fa-chevron-right text-xs opacity-40 group-hover:opacity-70 transition-opacity"></i>
              </router-link>
            </nav>
          </div>
        </aside>

        <!-- Content -->
        <div class="flex-1 min-w-0 p-4 lg:p-8">
          <!-- Loading bar -->
          <div v-if="loading" class="w-full flex justify-center py-2 mb-4">
            <div class="animate-pulse text-sm text-gray-400">Loading dashboard data...</div>
          </div>

          <router-view
            :summary="filteredSummary"
            :jobsOverTime="filteredJobsOverTime"
            :regionMetrics="regionMetrics"
            :customerMetrics="customerMetrics"
            :geoPoints="filteredGeoPoints"
            :testers="testers"
            :cancelledLost="cancelledLost"
            :loading="loading"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavMenu from "@/components/headers/navMenu";
import { Datetime } from "vue-datetime";
import "vue-datetime/dist/vue-datetime.css";
import axios from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/api/v1/dashboard";

export default {
  components: {
    NavMenu,
    Datetime,
  },
  data() {
    return {
      dateFrom: "",
      dateTo: "",
      activePreset: "This Month",
      includeSaturday: true,
      includeSunday: true,
      loading: false,

      // API data
      summary: {},
      jobsOverTime: [],
      regionMetrics: [],
      customerMetrics: [],
      geoPoints: [],
      testers: [],
      cancelledLost: {},

      // Presets
      presets: [
        { label: "Today", from: () => this.$moment().startOf("day"), to: () => this.$moment().endOf("day") },
        { label: "This Week", from: () => this.$moment().startOf("isoWeek"), to: () => this.$moment().endOf("isoWeek") },
        { label: "This Month", from: () => this.$moment().startOf("month"), to: () => this.$moment().endOf("month") },
        { label: "Last 90 Days", from: () => this.$moment().subtract(90, "days").startOf("day"), to: () => this.$moment().endOf("day") },
      ],

      // Side panel nav items
      navItems: [
        { label: "Overview", route: "DashboardOverview", helper: "KPIs and job trends" },
        { label: "Customers", route: "DashboardCustomers", helper: "Top accounts and regions" },
        { label: "Map", route: "DashboardMap", helper: "Job density heat map" },
        { label: "Staff", route: "DashboardStaff", helper: "Tester performance" },
      ],
    };
  },
  computed: {
    excludedDays() {
      const days = [];
      if (!this.includeSaturday) days.push(6);
      if (!this.includeSunday) days.push(0);
      return days;
    },
    filteredJobsOverTime() {
      if (!this.excludedDays.length) return this.jobsOverTime;
      return this.jobsOverTime.filter(d => {
        const dow = this.$moment(d.date).day();
        return !this.excludedDays.includes(dow);
      });
    },
    filteredSummary() {
      if (!this.excludedDays.length) return this.summary;
      const filtered = this.filteredJobsOverTime;
      if (!filtered.length) return this.summary;
      const total = filtered.reduce((s, d) => s + (d.total || 0), 0);
      const byStatus = {};
      filtered.forEach(d => {
        if (d.completed) byStatus["COMPLETED"] = (byStatus["COMPLETED"] || 0) + d.completed;
        if (d.cancelled) byStatus["CANCELLED"] = (byStatus["CANCELLED"] || 0) + d.cancelled;
        const other = (d.total || 0) - (d.completed || 0) - (d.cancelled || 0);
        if (other > 0) byStatus["OTHER"] = (byStatus["OTHER"] || 0) + other;
      });
      // Preserve any status keys from original summary that we can't derive from jobsOverTime
      if (this.summary.byStatus) {
        const derivedTotal = Object.values(byStatus).reduce((s, v) => s + v, 0);
        if (derivedTotal > 0) {
          return { ...this.summary, total, byStatus };
        }
      }
      return { ...this.summary, total };
    },
    filteredGeoPoints() {
      if (!this.excludedDays.length) return this.geoPoints;
      return this.geoPoints.filter(p => {
        if (!p.date) return true;
        const dow = this.$moment(p.date).day();
        return !this.excludedDays.includes(dow);
      });
    },
  },
  watch: {
    dateFrom() { this.debouncedFetch(); },
    dateTo() { this.debouncedFetch(); },
  },
  methods: {
    debouncedFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => this.fetchAll(), 50);
    },

    applyPreset(preset) {
      this.activePreset = preset.label;
      this.dateFrom = preset.from().toISOString();
      this.dateTo = preset.to().toISOString();
    },

    async getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          user ? resolve(user) : reject(new Error("No user is signed in."));
        });
      });
    },

    async fetchAll() {
      if (!this.dateFrom || !this.dateTo) return;
      this.loading = true;

      const user = await this.getCurrentUser();
      const token = await user.getIdToken();
      const headers = { Authorization: "Bearer " + token };
      const params = {
        dateFrom: this.$moment(this.dateFrom).startOf("day").toISOString(),
        dateTo: this.$moment(this.dateTo).endOf("day").toISOString(),
      };

      const safe = (promise) => promise.catch(e => { console.warn("Dashboard endpoint failed:", e.message); return null; });

      try {
        const [summaryRes, jobsRes, regionRes, customerRes, geoRes, testerRes, clRes] = await Promise.all([
          safe(axios.get(API_BASE + "/summary", { headers, params })),
          safe(axios.get(API_BASE + "/jobs-over-time", { headers, params })),
          safe(axios.get(API_BASE + "/region-metrics", { headers, params })),
          safe(axios.get(API_BASE + "/customer-metrics", { headers, params: { ...params, limit: 10 } })),
          safe(axios.get(API_BASE + "/geo-points", { headers, params })),
          safe(axios.get(API_BASE + "/tester-summary", { headers, params })),
          safe(axios.get(API_BASE + "/cancelled-lost", { headers, params })),
        ]);

        this.summary = (summaryRes && summaryRes.data.data) || {};
        this.jobsOverTime = (jobsRes && jobsRes.data.data) || [];
        this.regionMetrics = (regionRes && regionRes.data.data) || [];
        this.customerMetrics = (customerRes && customerRes.data.data) || [];
        this.geoPoints = (geoRes && geoRes.data.data) || [];
        this.testers = (testerRes && testerRes.data.data && testerRes.data.data.testers) || [];
        this.cancelledLost = (clRes && clRes.data.data) || {};
      } catch (e) {
        console.error("Dashboard fetch error:", e);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    // Default to "This Month"
    this.dateFrom = this.$moment().startOf("month").toISOString();
    this.dateTo = this.$moment().endOf("month").toISOString();
  },
};
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
